import React, { useState, useEffect } from "react"
import { connect } from "react-redux"
import { createStructuredSelector } from "reselect"
import { useHistory } from "react-router-dom"
import { loadStripe } from "@stripe/stripe-js"
import api from "../../services/api"

import {
  selectCartItems,
  selectCartTotal,
  selectCartCount,
} from "../../redux/cart/cart.selectors"

import { selectCurrentUser } from "../../redux/user/user.selectors"

import {
  Grid,
  Button,
  TextField,
  Modal,
  Backdrop,
  Fade,
} from "@material-ui/core"

import { HashLink as Link } from "react-router-hash-link"

import CheckoutItem from "../../components/CheckoutItem/CheckoutItem"
import { selectLanguage } from "../../redux/language/language.selectors"

import EN_DATA from "../../data/language/english.data"
import PT_DATA from "../../data/language/portuguese.data"

import "./Cart.scss"

const isComboItem = (item) => item?.is_combo || item?.isCombo

const normalizeCartForCheckout = (cart) => {
  return (Array.isArray(cart) ? cart : []).map((item) => ({
    ...item,
    quantity: isComboItem(item) ? 1 : item.quantity || 1,
  }))
}

const calculateCheckoutTotal = (cart) => {
  return normalizeCartForCheckout(cart).reduce((acc, item) => {
    const qty = item.quantity || 1
    const price = Number(item.price || 0)

    return acc + price * qty
  }, 0)
}

const getItemKey = (item) => {
  const dateKey =
    item?.dateSelected?.day && item?.dateSelected?.hour
      ? `${item.dateSelected.day}|${item.dateSelected.hour}`
      : ""
  return `${item._id}__${dateKey}`
}

const formatDate = (date) => {
  if (!date) return ""
  const d = new Date(date)
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`
}

function Cart({ cart, cartTotal, cartCount, language, currentUser }) {
  const text = language === "EN" ? EN_DATA.sections.cart : PT_DATA.sections.cart

  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY)

  const [voucher, setVoucher] = useState(localStorage.getItem("voucher") || "")
  const [checkTotal, setCheckTotal] = useState(cartTotal)
  const [isValid, setIsValid] = useState(false)
  const [isVoucherFree, setIsVoucherFree] = useState(false)

  const [submitting, setSubmitting] = useState(false)
  const [openConfirm, setOpenConfirm] = useState(false)

  const history = useHistory()

  useEffect(() => {
    if (isValid) return

    setCheckTotal(calculateCheckoutTotal(cart))
  }, [cart, isValid])

  // OPEN MODAL
  const handleOpenConfirm = () => {
    if (!cart?.length) return
    setOpenConfirm(true)
  }

  const handleCloseConfirm = () => setOpenConfirm(false)

  // FINAL CONFIRM
  async function handleConfirmPurchase() {
    if (submitting) return

    if (!currentUser) {
      history.push("/login")
      return
    }

    const totalCents = Math.round(Number(checkTotal) * 100)
    const userId = currentUser?.id || localStorage.getItem("userId")

    if (!userId) {
      history.push("/login")
      return
    }

    setSubmitting(true)

    const safeCart = normalizeCartForCheckout(cart)

    try {
      localStorage.setItem("userCart", JSON.stringify(safeCart))

      if (totalCents === 0) {
        await api.post("/free-checkout", {
          userId,
          cart: safeCart,
          voucher,
        })

        history.push("/success")
        return
      }

      const stripe = await stripePromise
      const session = await api.post("/payment-session", {
        total: totalCents,
        userId,
        voucher,
        cart: safeCart,
      })

      await stripe.redirectToCheckout({ sessionId: session.data.id })
    } catch (err) {
      console.error(err)
      alert(
        err?.response?.data?.message ||
          "Something went wrong with your booking.",
      )
      setSubmitting(false)
    }
  }

  // ---------------------------
  // VOUCHER
  // ---------------------------

  async function getVoucher(scope) {
    const code = (voucher || "").trim().toUpperCase()
    if (!code) return null

    try {
      const resp = await api.get("/voucher-validate", {
        params: {
          code,
          scope,
        },
      })

      return resp.data?.voucher || null
    } catch (err) {
      return null
    }
  }

  function applyVoucher(voucherObj, course) {
    if (!voucherObj) {
      setVoucher("")
      setIsValid(false)
      setIsVoucherFree(false)
      setCheckTotal(cartTotal)
      localStorage.removeItem("voucher")
      return
    }

    const isFree = voucherObj.discount_type === "free"

    if (isFree) {
      setIsVoucherFree(true)
      setCheckTotal(0)
      setIsValid(true)
      localStorage.setItem("voucher", voucherObj.code || "")
      return
    }

    const discount = Number(voucherObj.discount_value || 0)
    const total = calculateCheckoutTotal(cart)

    setCheckTotal(Math.max(0, total - discount))
    setIsVoucherFree(false)
    setIsValid(true)
    localStorage.setItem("voucher", voucherObj.code || "")
  }

  async function handleValidVoucher(event) {
    event.preventDefault()

    if (!cart || cart.length === 0) return

    const comboCourse = cart.find((course) => course.is_combo)
    if (comboCourse) {
      const applied = await getVoucher("combo")
      applyVoucher(applied, comboCourse)
      return
    }

    const onlineCourse = cart.find((course) => course.type === "online")
    if (onlineCourse) {
      const applied = await getVoucher("online")
      applyVoucher(applied, onlineCourse)
      return
    }

    const applied = await getVoucher("general")
    applyVoucher(applied, { price: cartTotal })
  }

  // ---------------------------
  // RENDER
  // ---------------------------

  return (
    <div className="cart container">
      <section>
        <h1>{text[0]}</h1>
        <p>
          {cartCount > 0 ? cartCount : "0"} {text[1]}
        </p>
      </section>

      {cart?.length > 0 ? (
        <section className="cart-container">
          <Grid container spacing={4}>
            <Grid item sm={8} xs={12}>
              {normalizeCartForCheckout(cart).map((item) => (
                <CheckoutItem key={getItemKey(item)} cartItem={item} />
              ))}
            </Grid>

            <Grid item sm={4} xs={12}>
              <p>Total</p>
              <h1>€{checkTotal}</h1>

              {/* NOW OPENS MODAL */}
              <Button
                className="btn-purple"
                type="button"
                onClick={handleOpenConfirm}
              >
                {text[2]}
              </Button>

              <div className="voucher">
                <TextField
                  id="voucher"
                  className="input-voucher"
                  label={text[3]}
                  value={voucher}
                  onChange={(e) => setVoucher(e.target.value.toUpperCase())}
                  disabled={isValid}
                />

                <Button onClick={handleValidVoucher}>
                  {isValid ? text[4] : text[5]}
                </Button>
              </div>
            </Grid>
          </Grid>
        </section>
      ) : (
        <section className="cart-container">
          {" "}
          <Grid container spacing={4}>
            {" "}
            <Grid item xs={12}>
              {" "}
              <p>{text[6]}</p>{" "}
            </Grid>{" "}
            <Grid item xs={12}>
              {" "}
              <Button className="btn-orange" type="button">
                {" "}
                <Link to="/#services">{text[7]}</Link>{" "}
              </Button>{" "}
            </Grid>{" "}
          </Grid>{" "}
        </section>
      )}

      {/* ========================= */}
      {/* CONFIRM MODAL */}
      {/* ========================= */}
      <Modal
        open={openConfirm}
        onClose={handleCloseConfirm}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 300 }}
      >
        <Fade in={openConfirm}>
          <div className="confirm-modal-overlay">
            <div className="confirm-modal">
              <h2>Confirm your purchase</h2>

              {normalizeCartForCheckout(cart).map((item) => (
                <div key={getItemKey(item)} className="confirm-item">
                  <b>{item.name}</b>
                  <p>Qty: {item.quantity || 1}</p>

                  {item?.dateSelected?.day && (
                    <p>
                      Date: {formatDate(item.dateSelected.day)} -{" "}
                      {item.dateSelected.hour}
                    </p>
                  )}
                </div>
              ))}

              <h3>Total: €{checkTotal}</h3>

              <div className="confirm-actions">
                <Button onClick={handleCloseConfirm} disabled={submitting}>
                  Cancel
                </Button>

                <Button
                  className="btn-purple"
                  onClick={handleConfirmPurchase}
                  disabled={submitting}
                >
                  {submitting
                    ? "Processing..."
                    : Math.round(Number(checkTotal) * 100) === 0
                      ? "Confirm Booking"
                      : "Confirm & Pay"}
                </Button>
              </div>
            </div>
          </div>
        </Fade>
      </Modal>
    </div>
  )
}

const mapStateToProps = createStructuredSelector({
  cart: selectCartItems,
  cartTotal: selectCartTotal,
  cartCount: selectCartCount,
  language: selectLanguage,
  currentUser: selectCurrentUser,
})

export default connect(mapStateToProps)(Cart)
